/**
 * useFilesState - State management for the files page
 * Contains all file-related state, services, and handlers
 */

import { prop, Signal, computedOf, type Prop } from '@tempots/dom'
import {
  createProjectPersistentFiles,
  createProjectFolders,
} from '@/services/gateway'
import type { PersistentFilesService } from '@/services/persistent-files.service'
import type { FoldersService } from '@/services/folders.service'
import { validateFilename, getUploadErrorMessage } from '@/services/errors'
import type {
  FileResponse,
  ListFilesOptions,
} from '@/services/persistent-files.service'
import type { FolderResponse } from '@/services/types'
import { toast } from '@/components/ui'
import {
  formatBytes,
  formatDate,
  getFileTypeFromMime,
  type FileType,
} from '@/utils'

// ============================================================================
// Types
// ============================================================================

export interface FileDisplayItem {
  id: string
  hash: string
  name: string
  mimeType: string
  size: string
  createdAt: string
  fileType: FileType
  folderId: string
  checkbox: string
}

export interface FilesState {
  // File data
  files: Prop<FileDisplayItem[]>
  nextCursor: Prop<string | undefined>
  loadingMore: Prop<boolean>

  // Search and sort
  searchQuery: Prop<string>
  includeSubfolders: Prop<boolean>
  sortBy: Prop<'filename' | 'size' | 'createdAt'>
  sortOrder: Prop<'asc' | 'desc'>

  // Folder state
  selectedFolderId: Prop<string>
  folderPath: Prop<FolderResponse[]>
  sidebarCollapsed: Prop<boolean>
  folderTreeRefresh: Prop<number>

  // Upload state
  uploading: Prop<boolean>
  uploadProgress: Prop<number>
  uploadError: Prop<string | null>
  uploadingCount: Prop<number>
  uploadedCount: Prop<number>

  // Bulk selection state
  selectedFileHashes: Prop<Set<string>>
  bulkActionError: Prop<string | null>
  performingBulkAction: Prop<boolean>

  // Bulk action modals
  showBulkMoveModal: Prop<boolean>
  showBulkCopyModal: Prop<boolean>
  availableFolders: Prop<FolderResponse[]>
  loadingFolders: Prop<boolean>

  // Derived states (computed signals, read-only)
  hasUploadError: Signal<boolean>
  hasNoFiles: Signal<boolean>
  hasMorePages: Signal<boolean>
  hasFolderPath: Signal<boolean>
  hasSearchQuery: Signal<boolean>
  hasSelectedFiles: Signal<boolean>
  hasBulkActionError: Signal<boolean>
  selectedCount: Signal<number>
  allFilesSelected: Signal<boolean>
}

export interface FilesHandlers {
  loadFiles: (cursor?: string) => Promise<FileResponse[]>
  handleSearchChange: (value: string) => void
  handleIncludeSubfoldersChange: (checked: boolean) => void
  loadFolderPath: (folderId: string) => Promise<void>
  handleFolderSelect: (folderId: string) => void
  handleUpload: (fileList: FileList) => Promise<void>
  triggerFileInput: () => void
  toggleFileSelection: (fileHash: string) => void
  toggleSelectAll: () => void
  clearSelection: () => void
  loadFolders: () => Promise<void>
  handleBulkDelete: () => Promise<void>
  openBulkMoveModal: () => void
  openBulkCopyModal: () => void
  handleBulkMoveToFolder: (folderId: string) => Promise<void>
  handleBulkCopyToFolder: (folderId: string) => Promise<void>
  cleanupSearchDebounce: () => void
}

// ============================================================================
// Transform Function
// ============================================================================

export function transformFile(file: FileResponse): FileDisplayItem {
  return {
    id: file.hash,
    hash: file.hash,
    name: file.filename,
    mimeType: file.mimeType,
    size: formatBytes(file.size),
    createdAt: formatDate(file.createdAt),
    fileType: getFileTypeFromMime(file.mimeType),
    folderId: file.folderId || '',
    checkbox: '',
  }
}

// ============================================================================
// URL Helpers
// ============================================================================

function getInitialFolderId(): string {
  const params = new URLSearchParams(window.location.search)
  return params.get('folder') || ''
}

function updateUrlFolder(folderId: string): void {
  const url = new URL(window.location.href)
  if (folderId) {
    url.searchParams.set('folder', folderId)
  } else {
    url.searchParams.delete('folder')
  }
  window.history.replaceState({}, '', url.toString())
}

// ============================================================================
// State Factory
// ============================================================================

export function createFilesState(projectId: Signal<string>): {
  state: FilesState
  handlers: FilesHandlers
  initialLoad: Promise<FileResponse[]>
} {
  // Service memoization
  let persistentFilesService: PersistentFilesService | null = null
  let foldersService: FoldersService | null = null
  let lastProjectId = ''

  const getServices = () => {
    const pid = projectId.value
    if (!pid) {
      return { persistentFiles: null, folders: null }
    }
    if (pid !== lastProjectId) {
      persistentFilesService = createProjectPersistentFiles(pid)
      foldersService = createProjectFolders(pid)
      lastProjectId = pid
    }
    return { persistentFiles: persistentFilesService, folders: foldersService }
  }

  // Debounce timer for search
  let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null

  // ============================================================================
  // State Signals
  // ============================================================================

  const files = prop<FileDisplayItem[]>([])
  const nextCursor = prop<string | undefined>(undefined)
  const loadingMore = prop(false)
  const searchQuery = prop('')
  const includeSubfolders = prop(false)
  const sortBy = prop<'filename' | 'size' | 'createdAt'>('createdAt')
  const sortOrder = prop<'asc' | 'desc'>('desc')

  const selectedFolderId = prop(getInitialFolderId())
  const folderPath = prop<FolderResponse[]>([])
  const sidebarCollapsed = prop(false)
  const folderTreeRefresh = prop(0)

  const uploading = prop(false)
  const uploadProgress = prop(0)
  const uploadError = prop<string | null>(null)
  const uploadingCount = prop(0)
  const uploadedCount = prop(0)

  const selectedFileHashes = prop<Set<string>>(new Set())
  const bulkActionError = prop<string | null>(null)
  const performingBulkAction = prop(false)

  const showBulkMoveModal = prop(false)
  const showBulkCopyModal = prop(false)
  const availableFolders = prop<FolderResponse[]>([])
  const loadingFolders = prop(false)

  // Derived states
  const hasUploadError = uploadError.map((e) => e !== null)
  const hasNoFiles = files.map((f) => f.length === 0)
  const hasMorePages = nextCursor.map((c) => c !== undefined)
  const hasFolderPath = folderPath.map((p) => p.length > 0)
  const hasSearchQuery = searchQuery.map((q) => q.trim().length > 0)
  const hasSelectedFiles = selectedFileHashes.map((s) => s.size > 0)
  const hasBulkActionError = bulkActionError.map((e) => e !== null)
  const selectedCount = selectedFileHashes.map((s) => s.size)
  const allFilesSelected = computedOf(
    files,
    selectedFileHashes
  )((f, s) => f.length > 0 && f.every((file) => s.has(file.hash)))

  // ============================================================================
  // Handlers
  // ============================================================================

  const loadFiles = async (cursor?: string): Promise<FileResponse[]> => {
    if (cursor) {
      loadingMore.set(true)
    }

    const folderId = selectedFolderId.get()
    const search = searchQuery.get().trim()
    const searchSubfolders = includeSubfolders.get()

    const { persistentFiles } = getServices()
    if (!persistentFiles) {
      throw new Error('No project selected')
    }
    const response = await persistentFiles.list({
      limit: 20,
      cursor,
      sort: sortBy.get() as ListFilesOptions['sort'],
      order: sortOrder.get() as ListFilesOptions['order'],
      folderId: folderId || undefined,
      search: search || undefined,
      includeSubfolders: searchSubfolders || undefined,
    })

    // eslint-disable-next-line tempots/require-async-signal-disposal -- plain array, not signal
    const transformedFiles = response.files.map(transformFile)

    if (cursor) {
      files.set([...files.get(), ...transformedFiles])
      loadingMore.set(false)
    } else {
      files.set(transformedFiles)
    }
    nextCursor.set(response.nextCursor)
    return response.files
  }

  const handleSearchChange = (value: string) => {
    searchQuery.set(value)
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer)
    }
    searchDebounceTimer = setTimeout(() => {
      loadFiles()
    }, 300)
  }

  const handleIncludeSubfoldersChange = (checked: boolean) => {
    includeSubfolders.set(checked)
    loadFiles()
  }

  const loadFolderPath = async (folderId: string) => {
    if (!folderId) {
      folderPath.set([])
      return
    }

    const { folders } = getServices()
    if (!folders) {
      folderPath.set([])
      return
    }

    try {
      const path: FolderResponse[] = []
      let currentId = folderId
      const MAX_FOLDER_DEPTH = 100
      let depth = 0

      while (currentId && depth < MAX_FOLDER_DEPTH) {
        const folder = await folders.get(currentId)
        path.unshift(folder)
        currentId = folder.parentId || ''
        depth++
      }

      if (depth >= MAX_FOLDER_DEPTH) {
        console.warn('Max folder depth reached, possible circular reference')
      }

      folderPath.set(path)
    } catch {
      folderPath.set([])
    }
  }

  const handleFolderSelect = (folderId: string) => {
    selectedFolderId.set(folderId)
    updateUrlFolder(folderId)
    loadFolderPath(folderId)
    loadFiles()
  }

  const handleUpload = async (fileList: FileList) => {
    const filesToUpload = Array.from(fileList)
    if (filesToUpload.length === 0) return

    const { persistentFiles } = getServices()
    if (!persistentFiles) {
      uploadError.set('No project selected')
      return
    }

    try {
      uploading.set(true)
      uploadProgress.set(0)
      uploadError.set(null)
      uploadingCount.set(filesToUpload.length)
      uploadedCount.set(0)

      const errors: string[] = []
      const currentFolderId = selectedFolderId.get()

      for (let i = 0; i < filesToUpload.length; i++) {
        const file = filesToUpload[i]

        const validationError = validateFilename(file.name)
        if (validationError) {
          errors.push(`${file.name}: ${validationError}`)
          continue
        }

        try {
          await persistentFiles.upload(file, file.name, {
            onProgress: (loaded: number, total: number) => {
              const fileProgress = loaded / total
              const overallProgress =
                ((i + fileProgress) / filesToUpload.length) * 100
              uploadProgress.set(Math.round(overallProgress))
            },
            folderId: currentFolderId || undefined,
          })

          uploadedCount.set(i + 1)
        } catch (e) {
          errors.push(getUploadErrorMessage(e, file.name))
        }
      }

      if (errors.length > 0) {
        uploadError.set(errors.join('\n'))
      }

      await loadFiles()
      folderTreeRefresh.set(folderTreeRefresh.get() + 1)
    } finally {
      uploading.set(false)
      uploadProgress.set(0)
      uploadingCount.set(0)
      uploadedCount.set(0)
    }
  }

  const triggerFileInput = () => {
    const input = document.getElementById(
      'file-upload-input'
    ) as HTMLInputElement
    input?.click()
  }

  const toggleFileSelection = (fileHash: string) => {
    const current = new Set(selectedFileHashes.get())
    if (current.has(fileHash)) {
      current.delete(fileHash)
    } else {
      current.add(fileHash)
    }
    selectedFileHashes.set(current)
  }

  const toggleSelectAll = () => {
    const current = files.get()
    const selected = selectedFileHashes.get()

    if (current.every((file) => selected.has(file.hash))) {
      selectedFileHashes.set(new Set())
    } else {
      selectedFileHashes.set(new Set(current.map((f) => f.hash)))
    }
  }

  const clearSelection = () => {
    selectedFileHashes.set(new Set())
  }

  const loadFolders = async () => {
    const { folders } = getServices()
    if (!folders) {
      return
    }

    try {
      loadingFolders.set(true)
      const response = await folders.list({ limit: 100 })
      availableFolders.set(response.folders)
    } catch {
      // Ignore errors loading folders
    } finally {
      loadingFolders.set(false)
    }
  }

  const handleBulkDelete = async () => {
    const selected = Array.from(selectedFileHashes.get())
    if (selected.length === 0) return

    const { persistentFiles } = getServices()
    if (!persistentFiles) {
      bulkActionError.set('No project selected')
      return
    }

    try {
      performingBulkAction.set(true)
      bulkActionError.set(null)

      const errors: string[] = []
      for (const hash of selected) {
        try {
          await persistentFiles.delete(hash)
        } catch (e) {
          const fileName =
            files.get().find((f) => f.hash === hash)?.name || hash
          errors.push(
            `${fileName}: ${e instanceof Error ? e.message : 'Failed to delete'}`
          )
        }
      }

      if (errors.length > 0) {
        bulkActionError.set(
          `Some files could not be deleted:\n${errors.join('\n')}`
        )
        toast.error(`${errors.length} file(s) could not be deleted`)
      } else {
        toast.success(`${selected.length} file(s) deleted`)
      }

      await loadFiles()
      folderTreeRefresh.set(folderTreeRefresh.get() + 1)
      clearSelection()
    } finally {
      performingBulkAction.set(false)
    }
  }

  const openBulkMoveModal = () => {
    if (selectedFileHashes.get().size === 0) return
    showBulkMoveModal.set(true)
    bulkActionError.set(null)
    loadFolders()
  }

  const openBulkCopyModal = () => {
    if (selectedFileHashes.get().size === 0) return
    showBulkCopyModal.set(true)
    bulkActionError.set(null)
    loadFolders()
  }

  const handleBulkMoveToFolder = async (folderId: string) => {
    const selected = Array.from(selectedFileHashes.get())
    if (selected.length === 0) return

    const { persistentFiles } = getServices()
    if (!persistentFiles) {
      bulkActionError.set('No project selected')
      return
    }

    try {
      performingBulkAction.set(true)
      bulkActionError.set(null)

      const errors: string[] = []
      for (const hash of selected) {
        try {
          await persistentFiles.update(hash, { folderId })
        } catch (e) {
          const fileName =
            files.get().find((f) => f.hash === hash)?.name || hash
          errors.push(
            `${fileName}: ${e instanceof Error ? e.message : 'Failed to move'}`
          )
        }
      }

      if (errors.length > 0) {
        bulkActionError.set(
          `Some files could not be moved:\n${errors.join('\n')}`
        )
      } else {
        showBulkMoveModal.set(false)
        clearSelection()
      }

      await loadFiles()
      folderTreeRefresh.set(folderTreeRefresh.get() + 1)
    } finally {
      performingBulkAction.set(false)
    }
  }

  const handleBulkCopyToFolder = async (folderId: string) => {
    const selected = Array.from(selectedFileHashes.get())
    if (selected.length === 0) return

    const { persistentFiles } = getServices()
    if (!persistentFiles) {
      bulkActionError.set('No project selected')
      return
    }

    try {
      performingBulkAction.set(true)
      bulkActionError.set(null)

      const errors: string[] = []
      for (const hash of selected) {
        try {
          const fileInfo = files.get().find((f) => f.hash === hash)
          if (!fileInfo) continue

          const contentUrl = persistentFiles.getContentUrl(hash)
          const response = await fetch(contentUrl)
          if (!response.ok) {
            throw new Error('Failed to download file')
          }

          const blob = await response.blob()
          const copyName = `Copy of ${fileInfo.name}`
          const uploadedFile = await persistentFiles.upload(blob, copyName)

          if (folderId) {
            await persistentFiles.update(uploadedFile.hash, { folderId })
          }
        } catch (e) {
          const fileName =
            files.get().find((f) => f.hash === hash)?.name || hash
          errors.push(
            `${fileName}: ${e instanceof Error ? e.message : 'Failed to copy'}`
          )
        }
      }

      if (errors.length > 0) {
        bulkActionError.set(
          `Some files could not be copied:\n${errors.join('\n')}`
        )
      } else {
        showBulkCopyModal.set(false)
        clearSelection()
      }

      await loadFiles()
      folderTreeRefresh.set(folderTreeRefresh.get() + 1)
    } finally {
      performingBulkAction.set(false)
    }
  }

  const cleanupSearchDebounce = () => {
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer)
    }
  }

  // Initial load
  const initialFolderId = getInitialFolderId()
  if (initialFolderId) {
    loadFolderPath(initialFolderId)
  }
  const initialLoad = loadFiles()

  // ============================================================================
  // Return State and Handlers
  // ============================================================================

  return {
    state: {
      files,
      nextCursor,
      loadingMore,
      searchQuery,
      includeSubfolders,
      sortBy,
      sortOrder,
      selectedFolderId,
      folderPath,
      sidebarCollapsed,
      folderTreeRefresh,
      uploading,
      uploadProgress,
      uploadError,
      uploadingCount,
      uploadedCount,
      selectedFileHashes,
      bulkActionError,
      performingBulkAction,
      showBulkMoveModal,
      showBulkCopyModal,
      availableFolders,
      loadingFolders,
      hasUploadError,
      hasNoFiles,
      hasMorePages,
      hasFolderPath,
      hasSearchQuery,
      hasSelectedFiles,
      hasBulkActionError,
      selectedCount,
      allFilesSelected,
    },
    handlers: {
      loadFiles,
      handleSearchChange,
      handleIncludeSubfoldersChange,
      loadFolderPath,
      handleFolderSelect,
      handleUpload,
      triggerFileInput,
      toggleFileSelection,
      toggleSelectAll,
      clearSelection,
      loadFolders,
      handleBulkDelete,
      openBulkMoveModal,
      openBulkCopyModal,
      handleBulkMoveToFolder,
      handleBulkCopyToFolder,
      cleanupSearchDebounce,
    },
    initialLoad,
  }
}
